import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Music, Disc3, Instagram, Facebook, Twitter, Youtube, 
  Globe, Loader2, Save, Clock, DollarSign, MapPin, Phone,
  CheckCircle, XCircle, AlertCircle, Sparkles, Play, Plus,
  Trash2, ExternalLink, Camera, FileText, Briefcase, Plane,
  Calendar, Settings, ChevronDown, ChevronUp, Award, Building2,
  PartyPopper, Link2, Mail, Mic2, Image, Timer, Star,
  Headphones, Disc, Radio, Music2, Users, Trophy, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { usePublicAuth } from '../../context/PublicAuthContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import ImageUpload from '../../components/ImageUpload';
import MultiImageUpload from '../../components/MultiImageUpload';
import OpenCallsSection from '../../components/dj/OpenCallsSection';
import AgencyRosterSection from '../../components/dj/AgencyRosterSection';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const GENRES = [
  'House', 'Techno', 'Tech House', 'Deep House', 'Progressive House',
  'Melodic Techno', 'Melodic House', 'Afro House', 'Tribal House',
  'Minimal', 'Trance', 'Psytrance', 'Progressive Trance',
  'Drum & Bass', 'Jungle', 'Breakbeat',
  'Dubstep', 'Bass Music', 'Future Bass',
  'Trap', 'Hip Hop', 'R&B',
  'Reggaeton', 'Latin', 'Cumbia', 'Dembow',
  'Pop', 'Indie Dance', 'Nu Disco',
  'Rock', 'Alternative',
  'Electrónica', 'Ambient', 'Downtempo',
  'Commercial', 'Top 40', 'Mashups',
  'Throwback/Retro', '80s/90s', 'Disco',
  'Open Format', 'Otro'
];

const PERFORMANCE_STYLES = [
  { value: 'digital', label: 'Digital (CDJs/USB)' },
  { value: 'vinyl', label: 'Vinyl Only' },
  { value: 'hybrid', label: 'Híbrido (Digital + Vinyl)' },
  { value: 'controller', label: 'Controladora' },
  { value: 'live', label: 'Live/Producción en vivo' },
  { value: 'turntablism', label: 'Turntablismo/Scratch' }
];

const SPECIAL_SKILLS = [
  'Scratching', 'Beat Juggling', 'Vinyl Mixing', 'Producción en vivo',
  'Remixing en vivo', 'VJing', 'MC/Hosting', 'Percusión en vivo',
  'Instrumentos en vivo', 'Beatmaking en vivo', 'Sampling en vivo'
];

// Countries with flags for nationality selector
const COUNTRIES = [
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'BR', name: 'Brasil', flag: '🇧🇷' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
  { code: 'MX', name: 'México', flag: '🇲🇽' },
  { code: 'PE', name: 'Perú', flag: '🇵🇪' },
  { code: 'UY', name: 'Uruguay', flag: '🇺🇾' },
  { code: 'PY', name: 'Paraguay', flag: '🇵🇾' },
  { code: 'EC', name: 'Ecuador', flag: '🇪🇨' },
  { code: 'VE', name: 'Venezuela', flag: '🇻🇪' },
  { code: 'BO', name: 'Bolivia', flag: '🇧🇴' },
  { code: 'ES', name: 'España', flag: '🇪🇸' },
  { code: 'US', name: 'Estados Unidos', flag: '🇺🇸' },
  { code: 'GB', name: 'Reino Unido', flag: '🇬🇧' },
  { code: 'DE', name: 'Alemania', flag: '🇩🇪' },
  { code: 'FR', name: 'Francia', flag: '🇫🇷' },
  { code: 'IT', name: 'Italia', flag: '🇮🇹' },
  { code: 'NL', name: 'Países Bajos', flag: '🇳🇱' },
  { code: 'BE', name: 'Bélgica', flag: '🇧🇪' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'JP', name: 'Japón', flag: '🇯🇵' },
  { code: 'KR', name: 'Corea del Sur', flag: '🇰🇷' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'CA', name: 'Canadá', flag: '🇨🇦' },
  { code: 'ZA', name: 'Sudáfrica', flag: '🇿🇦' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱' },
  { code: 'SE', name: 'Suecia', flag: '🇸🇪' },
  { code: 'OTHER', name: 'Otro', flag: '🌎' },
];

// Management types
const MANAGEMENT_TYPES = [
  { value: 'record_label', label: 'Sello Discográfico' },
  { value: 'agency', label: 'Agencia de Booking' },
  { value: 'manager', label: 'Manager Personal' },
  { value: 'production', label: 'Productora' },
  { value: 'collective', label: 'Colectivo/Crew' },
  { value: 'self', label: 'Independiente/Self-managed' },
];

const StatusBadge = ({ status }) => {
  const config = {
    pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock, text: 'Pendiente', fullText: 'Pendiente de Aprobación' },
    approved: { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle, text: 'Aprobada', fullText: 'Cuenta Aprobada' },
    rejected: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle, text: 'Rechazada', fullText: 'Solicitud Rechazada' }
  };
  const { color, icon: Icon, text, fullText } = config[status] || config.pending;
  
  return (
    <div className={`inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1 sm:py-2 rounded-full border ${color}`}>
      <Icon size={14} className="sm:w-4 sm:h-4" />
      <span className="font-medium text-xs sm:text-sm sm:hidden">{text}</span>
      <span className="font-medium text-xs sm:text-sm hidden sm:inline">{fullText}</span>
    </div>
  );
};

const SectionCard = ({ title, icon: Icon, children, defaultOpen = true, description, hidden = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  if (hidden) return null;
  return (
    <div className="bg-[#121212] border border-[#27272A] rounded-xl sm:rounded-2xl overflow-hidden">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 sm:p-5 hover:bg-[#1A1A1A] transition-colors"
      >
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
            <Icon size={16} className="sm:hidden text-[#D4AF37]" />
            <Icon size={20} className="hidden sm:block text-[#D4AF37]" />
          </div>
          <div className="text-left min-w-0">
            <h3 className="font-bold text-sm sm:text-lg truncate">{title}</h3>
            {description && <p className="text-[10px] sm:text-xs text-zinc-500 truncate">{description}</p>}
          </div>
        </div>
        <div className="flex-shrink-0 ml-2">
          {isOpen ? <ChevronUp size={18} className="sm:w-5 sm:h-5" /> : <ChevronDown size={18} className="sm:w-5 sm:h-5" />}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-3 sm:px-5 pb-4 sm:pb-5 border-t border-[#27272A] pt-4 sm:pt-5">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Dynamic list item component
const ListManager = ({ items, setItems, placeholder, icon: Icon, label }) => {
  const [newItem, setNewItem] = useState('');
  
  const addItem = () => {
    if (!newItem.trim()) return;
    setItems([...items, newItem.trim()]);
    setNewItem('');
  };
  
  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };
  
  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder={placeholder}
          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem())}
          className="bg-[#0A0A0A] border-[#27272A]"
        />
        <Button type="button" onClick={addItem} variant="outline" className="border-[#27272A]">
          <Plus size={16} />
        </Button>
      </div>
      {items?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-[#1A1A1A] rounded-lg text-sm">
              {Icon && <Icon size={14} className="text-[#D4AF37]" />}
              <span>{item}</span>
              <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DJProfilePage = () => {
  const { user, isAuthenticated } = usePublicAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  
  // Application form state
  const [applicationForm, setApplicationForm] = useState({
    applicant_type: 'dj',
    artistic_name: '',
    website: '',
    roster_size: '',
    contact_role: '',
    real_name: '',
    bio: '',
    genres: [],
    city: '',
    phone: '',
    email_contact: '',
    instagram: '',
    experience_years: ''
  });
  
  // Profile edit state
  const [profileForm, setProfileForm] = useState({});
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newRelease, setNewRelease] = useState({ title: '', year: '', label: '', link: '' });
  const [agencyDjs, setAgencyDjs] = useState([]);
  const [managedDj, setManagedDj] = useState(null); // roster DJ being edited by the agency
  const isAgencyForm = applicationForm.applicant_type === 'agency';

  const startEditingManagedDj = (dj) => {
    setManagedDj(dj);
    setProfile(dj);
    setProfileForm({ ...dj });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const exitManagedEditing = () => {
    setManagedDj(null);
    fetchProfile();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfile();
    }
  }, [isAuthenticated]);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/dj/my-profile');
      setHasProfile(response.data.has_profile);
      if (response.data.profile) {
        setProfile(response.data.profile);
        setProfileForm(response.data.profile);
      }
    } catch (error) {
      console.error('Error fetching DJ profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!isAgencyForm && applicationForm.genres.length === 0) {
      toast.error('Seleccioná al menos un género musical');
      return;
    }
    if (isAgencyForm) {
      const h = (applicationForm.handle || '').trim();
      if (!h || h.length < 3) {
        toast.error('Elegí un handle público para tu agencia');
        return;
      }
      if (applicationForm.handleStatus === 'taken') {
        toast.error('El handle elegido ya está en uso. Probá otro.');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const { handleStatus, handleReason, ...cleanForm } = applicationForm;
      const payload = {
        ...cleanForm,
        experience_years: applicationForm.experience_years ? Number(applicationForm.experience_years) : null,
      };
      const response = await api.post('/dj/apply', payload);
      console.log('DJ application response:', response.data);
      toast.success('¡Solicitud enviada! Te notificaremos cuando sea revisada.');
      fetchProfile();
    } catch (error) {
      console.error('DJ application error:', error.response?.data || error.message);
      const errorDetail = error.response?.data?.detail;
      let errorMsg = 'Error al enviar solicitud';
      if (typeof errorDetail === 'string') {
        errorMsg = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMsg = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateProfile = async () => {
    setSubmitting(true);
    try {
      const endpoint = managedDj ? `/dj/agency/my-djs/${managedDj.id}` : '/dj/my-profile';
      const response = await api.put(endpoint, profileForm);
      setProfile(response.data.profile);
      if (managedDj) setManagedDj(response.data.profile);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setSubmitting(false);
    }
  };

  const addVideoUrl = () => {
    if (!newVideoUrl.trim()) return;
    const videos = [...(profileForm.video_urls || []), newVideoUrl.trim()];
    setProfileForm({ ...profileForm, video_urls: videos });
    setNewVideoUrl('');
  };

  const removeVideoUrl = (index) => {
    const videos = profileForm.video_urls.filter((_, i) => i !== index);
    setProfileForm({ ...profileForm, video_urls: videos });
  };

  const addRelease = () => {
    if (!newRelease.title.trim()) return;
    const releases = [...(profileForm.releases || []), { ...newRelease }];
    setProfileForm({ ...profileForm, releases });
    setNewRelease({ title: '', year: '', label: '', link: '' });
  };

  const removeRelease = (index) => {
    const releases = profileForm.releases.filter((_, i) => i !== index);
    setProfileForm({ ...profileForm, releases });
  };

  const toggleGenre = (genre) => {
    if (profile) {
      const genres = profileForm.genres || [];
      const newGenres = genres.includes(genre) 
        ? genres.filter(g => g !== genre)
        : [...genres, genre];
      setProfileForm({ ...profileForm, genres: newGenres });
    } else {
      const newGenres = applicationForm.genres.includes(genre)
        ? applicationForm.genres.filter(g => g !== genre)
        : [...applicationForm.genres, genre];
      setApplicationForm({ ...applicationForm, genres: newGenres });
    }
  };

  const toggleSkill = (skill) => {
    const skills = profileForm.special_skills || [];
    const newSkills = skills.includes(skill) 
      ? skills.filter(s => s !== skill)
      : [...skills, skill];
    setProfileForm({ ...profileForm, special_skills: newSkills });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
        <div className="text-center max-w-md w-full">
          <Disc3 size={64} className="mx-auto mb-4 text-[#D4AF37]" />
          <h2 className="text-2xl font-bold mb-2 text-white" style={{ fontFamily: "'Unbounded', sans-serif" }}>Iniciá sesión</h2>
          <p className="text-zinc-400 mb-8">
            Para postularte a las convocatorias y crear tu perfil de DJ necesitás una cuenta gratuita de Bravo.
          </p>
          <div className="flex flex-col gap-3">
            <Link
              to={`/iniciar-sesion?redirect=${encodeURIComponent('/portal-dj')}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#D4AF37] text-black text-sm font-black uppercase tracking-widest hover:bg-[#F2D272] transition-colors"
              data-testid="dj-portal-login-btn"
            >
              Iniciar sesión / Crear cuenta
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white/70 text-sm font-semibold hover:border-white/40 hover:text-white transition-colors"
              data-testid="dj-portal-back-home-btn"
            >
              Volver al inicio
            </Link>
          </div>
          <p className="mt-6 text-[11px] text-zinc-600">
            Después de iniciar sesión volvés directo al Portal DJ para completar tu postulación.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  // Portal DJ now open to everyone (no FULL BRAVX gate)

  // Application form (no profile yet)
  if (!hasProfile) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center">
              <Disc3 size={40} className="text-black" />
            </div>
            <h1 className="text-3xl font-black mb-3">Portal DJ</h1>
            <p className="text-zinc-400 max-w-lg mx-auto">
              Creá tu cuenta DJ profesional y hacé visible tu Press Kit para productoras de todo el país.
              Completá el formulario con tu información básica para comenzar.
            </p>
          </div>

          {/* Active open calls teaser (before having a profile) */}
          <OpenCallsSection canApply={false} />

          <form onSubmit={handleApply} className="space-y-6">
            {/* Tipo de cuenta: DJ o Agencia */}
            <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 space-y-4" data-testid="applicant-type-section">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Users size={20} className="text-[#D4AF37]" />
                ¿Quién se registra?
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setApplicationForm({ ...applicationForm, applicant_type: 'dj' })}
                  className={`p-4 rounded-xl border text-left transition-all ${!isAgencyForm ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#27272A] bg-[#0A0A0A] hover:border-[#3F3F46]'}`}
                  data-testid="applicant-type-dj"
                >
                  <Disc3 size={22} className={!isAgencyForm ? 'text-[#D4AF37]' : 'text-zinc-500'} />
                  <p className="font-bold mt-2 text-sm">DJ Particular</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Registrás tu propio perfil y Press Kit</p>
                </button>
                <button
                  type="button"
                  onClick={() => setApplicationForm({ ...applicationForm, applicant_type: 'agency' })}
                  className={`p-4 rounded-xl border text-left transition-all ${isAgencyForm ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#27272A] bg-[#0A0A0A] hover:border-[#3F3F46]'}`}
                  data-testid="applicant-type-agency"
                >
                  <Building2 size={22} className={isAgencyForm ? 'text-[#D4AF37]' : 'text-zinc-500'} />
                  <p className="font-bold mt-2 text-sm">Agencia de DJs</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Gestionás el roster de tus DJs desde un solo lugar</p>
                </button>
              </div>
              {isAgencyForm && (
                <p className="text-xs text-[#D4AF37]/80 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-lg p-3">
                  Una vez aprobada tu agencia, vas a poder agregar y gestionar los perfiles de tus DJs desde el portal.
                </p>
              )}
            </div>

            {isAgencyForm ? (
              <>
                {/* AGENCIA — Datos corporativos */}
                <div className="bg-[#121212] border border-[#27272A] rounded-2xl overflow-hidden">
                  <div className="bg-gradient-to-r from-[#D4AF37]/15 to-transparent border-b border-[#27272A] px-6 py-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                      <Building2 size={20} className="text-black" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg leading-tight">Datos de la Agencia</h3>
                      <p className="text-[11px] text-zinc-500">Información corporativa para evaluar tu solicitud</p>
                    </div>
                  </div>
                  <div className="p-6 space-y-5">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre de la Agencia *</Label>
                        <Input
                          value={applicationForm.artistic_name}
                          onChange={(e) => setApplicationForm({...applicationForm, artistic_name: e.target.value})}
                          placeholder="Ej: Sunset Bookings"
                          required
                          className="bg-[#0A0A0A] border-[#27272A]"
                          data-testid="dj-artistic-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>
                          Handle público *
                          <span className="text-xs text-zinc-500 font-normal ml-1">(URL de tu perfil)</span>
                        </Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none">/agencia/</span>
                          <Input
                            value={applicationForm.handle || ''}
                            onChange={(e) => {
                              const clean = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').slice(0, 40);
                              setApplicationForm({...applicationForm, handle: clean, handleStatus: 'checking'});
                            }}
                            onBlur={async () => {
                              const h = (applicationForm.handle || '').trim();
                              if (!h || h.length < 3) {
                                setApplicationForm(f => ({...f, handleStatus: 'short'}));
                                return;
                              }
                              try {
                                const res = await api.get(`/dj/check-handle?handle=${encodeURIComponent(h)}`);
                                setApplicationForm(f => ({...f, handleStatus: res.data?.available ? 'ok' : 'taken', handleReason: res.data?.reason}));
                              } catch { /* ignore */ }
                            }}
                            placeholder="sunset-bookings"
                            required
                            className="bg-[#0A0A0A] border-[#27272A] pl-[76px]"
                            data-testid="agency-handle"
                          />
                        </div>
                        {applicationForm.handleStatus === 'ok' && (
                          <p className="text-[11px] text-emerald-400" data-testid="agency-handle-ok">
                            ✓ Disponible: bravo.com/agencia/{applicationForm.handle}
                          </p>
                        )}
                        {applicationForm.handleStatus === 'taken' && (
                          <p className="text-[11px] text-red-400" data-testid="agency-handle-taken">
                            Ese handle ya está en uso o reservado. Probá otro.
                          </p>
                        )}
                        {applicationForm.handleStatus === 'short' && (
                          <p className="text-[11px] text-yellow-400">Mínimo 3 caracteres válidos.</p>
                        )}
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Sitio Web</Label>
                        <Input
                          value={applicationForm.website}
                          onChange={(e) => setApplicationForm({...applicationForm, website: e.target.value})}
                          placeholder="https://tuagencia.com"
                          className="bg-[#0A0A0A] border-[#27272A]"
                          data-testid="agency-website"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email Corporativo *</Label>
                        <Input
                          type="email"
                          value={applicationForm.email_contact}
                          onChange={(e) => setApplicationForm({...applicationForm, email_contact: e.target.value})}
                          placeholder="bookings@tuagencia.com"
                          required
                          className="bg-[#0A0A0A] border-[#27272A]"
                          data-testid="dj-email"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Teléfono *</Label>
                        <Input
                          value={applicationForm.phone}
                          onChange={(e) => setApplicationForm({...applicationForm, phone: e.target.value})}
                          placeholder="+54 11 1234-5678"
                          required
                          className="bg-[#0A0A0A] border-[#27272A]"
                          data-testid="dj-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Ciudad *</Label>
                        <Input
                          value={applicationForm.city}
                          onChange={(e) => setApplicationForm({...applicationForm, city: e.target.value})}
                          placeholder="Ej: Buenos Aires, CABA"
                          required
                          className="bg-[#0A0A0A] border-[#27272A]"
                          data-testid="dj-city"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Instagram de la Agencia</Label>
                        <Input
                          value={applicationForm.instagram}
                          onChange={(e) => setApplicationForm({...applicationForm, instagram: e.target.value})}
                          placeholder="@tuagencia"
                          className="bg-[#0A0A0A] border-[#27272A]"
                          data-testid="dj-instagram"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Años en la industria</Label>
                        <Input
                          type="number"
                          value={applicationForm.experience_years}
                          onChange={(e) => setApplicationForm({...applicationForm, experience_years: e.target.value})}
                          placeholder="Ej: 8"
                          min="0"
                          max="50"
                          className="bg-[#0A0A0A] border-[#27272A]"
                          data-testid="agency-industry-years"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>DJs en el roster</Label>
                        <div className="flex flex-wrap gap-2" data-testid="agency-roster-size">
                          {['1-5', '6-15', '16-30', '+30'].map((size) => (
                            <button
                              key={size}
                              type="button"
                              onClick={() => setApplicationForm({...applicationForm, roster_size: size})}
                              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                                applicationForm.roster_size === size
                                  ? 'bg-[#D4AF37] text-black'
                                  : 'bg-[#27272A] text-zinc-400 hover:bg-[#3F3F46]'
                              }`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AGENCIA — Responsable de la cuenta */}
                <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 space-y-5">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <Briefcase size={20} className="text-[#D4AF37]" />
                    Responsable de la Cuenta
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nombre y Apellido *</Label>
                      <Input
                        value={applicationForm.real_name}
                        onChange={(e) => setApplicationForm({...applicationForm, real_name: e.target.value})}
                        placeholder="Nombre completo del responsable"
                        required
                        className="bg-[#0A0A0A] border-[#27272A]"
                        data-testid="dj-real-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cargo</Label>
                      <Input
                        value={applicationForm.contact_role}
                        onChange={(e) => setApplicationForm({...applicationForm, contact_role: e.target.value})}
                        placeholder="Ej: Director, Booking Manager"
                        className="bg-[#0A0A0A] border-[#27272A]"
                        data-testid="agency-contact-role"
                      />
                    </div>
                  </div>
                </div>

                {/* AGENCIA — Descripción */}
                <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 space-y-5">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <FileText size={20} className="text-[#D4AF37]" />
                    Sobre la Agencia
                  </h3>
                  <div className="space-y-2">
                    <Label>Descripción breve (opcional)</Label>
                    <Textarea
                      value={applicationForm.bio}
                      onChange={(e) => setApplicationForm({...applicationForm, bio: e.target.value})}
                      placeholder="Contanos sobre tu agencia: hace cuánto trabajan, qué tipo de artistas representan, en qué eventos participaron..."
                      rows={4}
                      className="bg-[#0A0A0A] border-[#27272A] resize-none"
                      data-testid="dj-bio"
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
            {/* Información Personal */}
            <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 space-y-5">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Music size={20} className="text-[#D4AF37]" />
                Información Personal
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre Artístico *</Label>
                  <Input
                    value={applicationForm.artistic_name}
                    onChange={(e) => setApplicationForm({...applicationForm, artistic_name: e.target.value})}
                    placeholder="Tu nombre como DJ"
                    required
                    className="bg-[#0A0A0A] border-[#27272A]"
                    data-testid="dj-artistic-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre Real *</Label>
                  <Input
                    value={applicationForm.real_name}
                    onChange={(e) => setApplicationForm({...applicationForm, real_name: e.target.value})}
                    placeholder="Tu nombre completo"
                    required
                    className="bg-[#0A0A0A] border-[#27272A]"
                    data-testid="dj-real-name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ciudad *</Label>
                  <Input
                    value={applicationForm.city}
                    onChange={(e) => setApplicationForm({...applicationForm, city: e.target.value})}
                    placeholder="Ej: Buenos Aires, CABA"
                    required
                    className="bg-[#0A0A0A] border-[#27272A]"
                    data-testid="dj-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono *</Label>
                  <Input
                    value={applicationForm.phone}
                    onChange={(e) => setApplicationForm({...applicationForm, phone: e.target.value})}
                    placeholder="+54 11 1234-5678"
                    required
                    className="bg-[#0A0A0A] border-[#27272A]"
                    data-testid="dj-phone"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email de Contacto Profesional *</Label>
                  <Input
                    type="email"
                    value={applicationForm.email_contact}
                    onChange={(e) => setApplicationForm({...applicationForm, email_contact: e.target.value})}
                    placeholder="booking@tunombre.com"
                    required
                    className="bg-[#0A0A0A] border-[#27272A]"
                    data-testid="dj-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Instagram</Label>
                  <Input
                    value={applicationForm.instagram}
                    onChange={(e) => setApplicationForm({...applicationForm, instagram: e.target.value})}
                    placeholder="@tu_usuario"
                    className="bg-[#0A0A0A] border-[#27272A]"
                    data-testid="dj-instagram"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Años de Experiencia</Label>
                <Input
                  type="number"
                  value={applicationForm.experience_years}
                  onChange={(e) => setApplicationForm({...applicationForm, experience_years: e.target.value})}
                  placeholder="Ej: 5"
                  min="0"
                  max="50"
                  className="bg-[#0A0A0A] border-[#27272A] max-w-xs"
                  data-testid="dj-experience"
                />
              </div>
            </div>

            {/* Biografía */}
            <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 space-y-5">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <FileText size={20} className="text-[#D4AF37]" />
                Biografía
              </h3>
              
              <div className="space-y-2">
                <Label>Contanos sobre tu carrera * (mínimo 100 caracteres)</Label>
                <Textarea
                  value={applicationForm.bio}
                  onChange={(e) => setApplicationForm({...applicationForm, bio: e.target.value})}
                  placeholder="Describí tu trayectoria, estilo musical, lugares donde tocaste, qué te hace único como DJ..."
                  rows={6}
                  required
                  minLength={100}
                  className="bg-[#0A0A0A] border-[#27272A] resize-none"
                  data-testid="dj-bio"
                />
                <div className="flex justify-between text-xs">
                  <p className={`${applicationForm.bio.length >= 100 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                    {applicationForm.bio.length}/100 caracteres mínimo
                  </p>
                  <p className="text-zinc-500">Sé descriptivo para destacar tu perfil</p>
                </div>
              </div>
            </div>
              </>
            )}

            {/* Géneros (solo DJ particular) */}
            {!isAgencyForm && (
            <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 space-y-5">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Headphones size={20} className="text-[#D4AF37]" />
                Géneros Musicales
              </h3>
              
              <div className="space-y-3">
                <Label>Seleccioná tus géneros principales * (al menos uno)</Label>
                <div className="flex flex-wrap gap-2">
                  {GENRES.map(genre => (
                    <button
                      key={genre}
                      type="button"
                      onClick={() => toggleGenre(genre)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        applicationForm.genres.includes(genre)
                          ? 'bg-[#D4AF37] text-black'
                          : 'bg-[#27272A] text-zinc-400 hover:bg-[#3F3F46]'
                      }`}
                      data-testid={`genre-${genre}`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
                {applicationForm.genres.length > 0 && (
                  <p className="text-sm text-emerald-400">
                    {applicationForm.genres.length} género(s) seleccionado(s)
                  </p>
                )}
              </div>
            </div>
            )}

            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
              <p className="text-sm text-yellow-200">
                <AlertCircle size={16} className="inline mr-2" />
                {isAgencyForm
                  ? 'Tu solicitud será revisada por el equipo de Bravo. Una vez aprobada tu agencia, vas a poder agregar tus DJs y completar el Press Kit de cada uno.'
                  : 'Tu solicitud será revisada por el equipo de Bravo. Una vez aprobada, podrás completar tu Press Kit completo con fotos, videos, rider técnico, tarifas y más.'}
              </p>
            </div>

            <Button 
              type="submit" 
              disabled={
                submitting ||
                (!isAgencyForm && (applicationForm.genres.length === 0 || applicationForm.bio.length < 100)) ||
                (isAgencyForm && (!applicationForm.handle || applicationForm.handle.length < 3 || applicationForm.handleStatus === 'taken' || applicationForm.handleStatus === 'short'))
              }
              className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold h-12 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="dj-submit"
            >
              {submitting ? (
                <><Loader2 size={18} className="animate-spin mr-2" /> Enviando...</>
              ) : (
                'Enviar Solicitud'
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Profile exists - show status or edit form
  const canEditPressKit = profile.status === 'approved' || !!managedDj;
  const isAgencyProfile = !managedDj && (profile.applicant_type || 'dj') === 'agency';
  return (
    <div className="min-h-screen bg-[#0A0A0A] py-6 sm:py-12 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        {/* Managed DJ editing banner */}
        {managedDj && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="managed-dj-banner">
            <div className="flex-1">
              <p className="text-[#D4AF37] font-bold text-sm">
                Estás editando el perfil de {managedDj.artistic_name} (DJ de tu agencia)
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">
                {managedDj.status === 'pending'
                  ? 'Este DJ todavía está pendiente de aprobación: podés ir completando su Press Kit mientras tanto.'
                  : 'Los cambios se publican en su Press Kit público.'}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={exitManagedEditing}
              className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              data-testid="back-to-agency-btn"
            >
              ← Volver a mi agencia
            </Button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="relative flex-shrink-0">
              {profileForm.photo_url ? (
                <img src={profileForm.photo_url} alt="" className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl object-cover" />
              ) : (
                <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center">
                  <Disc3 size={28} className="sm:hidden text-black" />
                  <Disc3 size={40} className="hidden sm:block text-black" />
                </div>
              )}
              {profile.featured && (
                <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-5 h-5 sm:w-6 sm:h-6 bg-[#D4AF37] rounded-full flex items-center justify-center">
                  <Star size={12} className="sm:hidden text-black" />
                  <Star size={14} className="hidden sm:block text-black" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl font-black truncate">{profile.artistic_name}</h1>
              <StatusBadge status={profile.status} />
            </div>
          </div>
          {canEditPressKit && (
            <Button 
              onClick={handleUpdateProfile}
              disabled={submitting}
              className="w-full sm:w-auto bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold"
              data-testid="save-profile"
            >
              {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
              Guardar Cambios
            </Button>
          )}
        </div>

        {/* Rejection message */}
        {profile.status === 'rejected' && !managedDj && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-5 mb-6">
            <h3 className="font-bold text-red-400 mb-2">Tu solicitud fue rechazada</h3>
            <p className="text-zinc-400 mb-3">{profile.rejection_reason}</p>
            <Button 
              onClick={() => { setHasProfile(false); setProfile(null); }}
              variant="outline"
              className="border-red-500/30 text-red-400"
            >
              Volver a aplicar
            </Button>
          </div>
        )}

        {/* Pending message */}
        {profile.status === 'pending' && !managedDj && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-5 mb-6">
            <h3 className="font-bold text-yellow-400 mb-2">Solicitud en revisión</h3>
            <p className="text-zinc-400">
              Tu solicitud está siendo revisada por el equipo de Bravo. 
              Te notificaremos por email cuando sea aprobada.
            </p>
          </div>
        )}

        {/* Open Calls (convocatorias) — visible to any profile holder */}
        {!managedDj && (
          <OpenCallsSection
            canApply={profile.status === 'approved'}
            isAgency={(profile.applicant_type || 'dj') === 'agency'}
            agencyDjs={agencyDjs}
            ownProfileId={profile.id}
          />
        )}

        {/* Agency roster management */}
        {!managedDj && profile.status === 'approved' && (profile.applicant_type || 'dj') === 'agency' && (
          <AgencyRosterSection onDjsLoaded={setAgencyDjs} onEditDj={startEditingManagedDj} />
        )}

        {/* Public Press Kit Link */}
        {profile.status === 'approved' && profile.slug && (() => {
          const isAgencyLink = !managedDj && (profile.applicant_type || 'dj') === 'agency';
          const publicPath = `${isAgencyLink ? '/agencia/' : '/dj/'}${profile.slug}`;
          return (
          <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-2xl p-5 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                  <Link2 size={20} className="text-[#D4AF37]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-white">{managedDj ? `Press Kit Público de ${profile.artistic_name}` : (isAgencyLink ? 'Perfil Público de la Agencia' : 'Tu Press Kit Público')}</p>
                  <p className="text-xs text-zinc-400 truncate" data-testid="dj-public-link-url">
                    {`${window.location.origin}${publicPath}`}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}${publicPath}`);
                    toast.success('Link copiado al portapapeles');
                  }}
                  className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold flex-1 sm:flex-initial"
                  data-testid="copy-presskit-link"
                >
                  Copiar Link
                </Button>
                <Button
                  onClick={() => window.open(publicPath, '_blank')}
                  variant="outline"
                  className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 flex-1 sm:flex-initial"
                  data-testid="view-presskit-link"
                >
                  <ExternalLink size={16} className="mr-1" />
                  Ver
                </Button>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Approved (or managed by agency) - Full Press Kit Editor */}
        {canEditPressKit && (
          <div className="space-y-6">
            {/* Profile Photo & Banner */}
            <SectionCard title={isAgencyProfile ? 'Logo y Banner' : 'Fotos de Perfil'} icon={Camera} description={isAgencyProfile ? 'Identidad visual de tu agencia' : 'Subí tus imágenes principales'}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Upload size={14} className="text-[#D4AF37]" />
                    Foto de Perfil (cuadrada)
                  </Label>
                  <ImageUpload
                    value={profileForm.photo_url}
                    onChange={(url) => setProfileForm({...profileForm, photo_url: url})}
                    folder="dj_profiles"
                    aspectRatio="square"
                    label="Subir foto de perfil"
                  />
                </div>
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Upload size={14} className="text-[#D4AF37]" />
                    Banner (1200x400 recomendado)
                  </Label>
                  <ImageUpload
                    value={profileForm.banner_url}
                    onChange={(url) => setProfileForm({...profileForm, banner_url: url})}
                    folder="dj_banners"
                    aspectRatio="banner"
                    label="Subir banner"
                  />
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-4">Las imágenes se suben automáticamente. Formatos: JPG, PNG, WebP, GIF (máx 10MB)</p>
            </SectionCard>

            {/* Basic Info */}
            <SectionCard title={isAgencyProfile ? 'Información de la Agencia' : 'Información Básica'} icon={isAgencyProfile ? Building2 : Music} description={isAgencyProfile ? 'Datos corporativos de tu agencia' : 'Datos esenciales de tu perfil'}>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isAgencyProfile ? 'Nombre de la Agencia' : 'Nombre Artístico'}</Label>
                  <Input
                    value={profileForm.artistic_name || ''}
                    onChange={(e) => setProfileForm({...profileForm, artistic_name: e.target.value})}
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{isAgencyProfile ? 'Responsable de la Cuenta' : 'Nombre Real'}</Label>
                  <Input
                    value={profileForm.real_name || ''}
                    onChange={(e) => setProfileForm({...profileForm, real_name: e.target.value})}
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label>Ciudad</Label>
                  <Input
                    value={profileForm.city || ''}
                    onChange={(e) => setProfileForm({...profileForm, city: e.target.value})}
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={profileForm.phone || ''}
                    onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
              </div>

              {/* NEW: Nationality (DJs) / Corporate data (agencies) */}
              {isAgencyProfile ? (
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label>DJs en el roster</Label>
                    <div className="flex flex-wrap gap-2">
                      {['1-5', '6-15', '16-30', '+30'].map((size) => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => setProfileForm({...profileForm, roster_size: size})}
                          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                            profileForm.roster_size === size
                              ? 'bg-[#D4AF37] text-black'
                              : 'bg-[#27272A] text-zinc-400 hover:bg-[#3F3F46]'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Cargo del responsable</Label>
                    <Input
                      value={profileForm.contact_role || ''}
                      onChange={(e) => setProfileForm({...profileForm, contact_role: e.target.value})}
                      placeholder="Ej: Director, Booking Manager"
                      className="bg-[#0A0A0A] border-[#27272A]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Años en la industria</Label>
                    <Input
                      type="number"
                      value={profileForm.experience_years || ''}
                      onChange={(e) => setProfileForm({...profileForm, experience_years: parseInt(e.target.value) || null})}
                      placeholder="8"
                      className="bg-[#0A0A0A] border-[#27272A]"
                    />
                  </div>
                </div>
              ) : (
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    🌎 Nacionalidad
                  </Label>
                  <Select 
                    value={profileForm.nationality || ''} 
                    onValueChange={(v) => {
                      const country = COUNTRIES.find(c => c.code === v);
                      setProfileForm({
                        ...profileForm, 
                        nationality: v, 
                        nationality_name: country?.name || ''
                      });
                    }}
                  >
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                      <SelectValue placeholder="Seleccionar país">
                        {profileForm.nationality && (
                          <span className="flex items-center gap-2">
                            {COUNTRIES.find(c => c.code === profileForm.nationality)?.flag}
                            {COUNTRIES.find(c => c.code === profileForm.nationality)?.name}
                          </span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(country => (
                        <SelectItem key={country.code} value={country.code}>
                          <span className="flex items-center gap-2">
                            {country.flag} {country.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Años de Experiencia</Label>
                  <Input
                    type="number"
                    value={profileForm.experience_years || ''}
                    onChange={(e) => setProfileForm({...profileForm, experience_years: parseInt(e.target.value) || null})}
                    placeholder="5"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
              </div>
              )}

              {/* NEW: Management / Label (not for agencies, hidden for agency-linked DJs) */}
              {!isAgencyProfile && (managedDj || profile.agency_id) && (
                <div className="mt-4 p-4 bg-[#0A0A0A] rounded-xl border border-[#27272A] flex items-start gap-3" data-testid="agency-managed-note">
                  <Building2 size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-white">Representación / Management</p>
                    <p className="text-zinc-500 text-xs mt-1">
                      Este perfil está vinculado a {profile.managed_by || managedDj?.managed_by || 'su agencia'} — la representación se muestra automáticamente en el press kit.
                    </p>
                  </div>
                </div>
              )}
              {!isAgencyProfile && !managedDj && !profile.agency_id && (
              <div className="mt-4 p-4 bg-[#0A0A0A] rounded-xl border border-[#27272A]">
                <Label className="flex items-center gap-2 mb-3">
                  <Building2 size={16} className="text-purple-400" />
                  Representación / Management
                </Label>
                <p className="text-zinc-500 text-xs mb-4">
                  Discográfica, agencia de booking o manager que te representa (opcional)
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-500">Tipo</Label>
                    <Select 
                      value={profileForm.management?.type || ''} 
                      onValueChange={(v) => setProfileForm({
                        ...profileForm, 
                        management: {...(profileForm.management || {}), type: v}
                      })}
                    >
                      <SelectTrigger className="bg-[#121212] border-[#27272A]">
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {MANAGEMENT_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-500">Nombre</Label>
                    <Input
                      value={profileForm.management?.name || ''}
                      onChange={(e) => setProfileForm({
                        ...profileForm, 
                        management: {...(profileForm.management || {}), name: e.target.value}
                      })}
                      placeholder="Ej: Cercle Records, ICM Partners..."
                      className="bg-[#121212] border-[#27272A]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-500">Contacto</Label>
                    <Input
                      value={profileForm.management?.contact || ''}
                      onChange={(e) => setProfileForm({
                        ...profileForm, 
                        management: {...(profileForm.management || {}), contact: e.target.value}
                      })}
                      placeholder="booking@label.com"
                      className="bg-[#121212] border-[#27272A]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-500">Website</Label>
                    <Input
                      value={profileForm.management?.website || ''}
                      onChange={(e) => setProfileForm({
                        ...profileForm, 
                        management: {...(profileForm.management || {}), website: e.target.value}
                      })}
                      placeholder="https://label.com"
                      className="bg-[#121212] border-[#27272A]"
                    />
                  </div>
                </div>
              </div>
              )}

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Mail size={14} /> Email de Contacto</Label>
                  <Input
                    type="email"
                    value={profileForm.email_contact || ''}
                    onChange={(e) => setProfileForm({...profileForm, email_contact: e.target.value})}
                    placeholder="booking@tunombre.com"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Globe size={14} /> Website</Label>
                  <Input
                    value={profileForm.website || ''}
                    onChange={(e) => setProfileForm({...profileForm, website: e.target.value})}
                    placeholder="https://tunombre.com"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <Label>{isAgencyProfile ? 'Descripción de la Agencia' : 'Biografía'}</Label>
                <Textarea
                  value={profileForm.bio || ''}
                  onChange={(e) => setProfileForm({...profileForm, bio: e.target.value})}
                  rows={5}
                  className="bg-[#0A0A0A] border-[#27272A] resize-none"
                />
              </div>
              {!isAgencyProfile && (
              <div className="space-y-3 mt-4">
                <Label>Géneros Musicales</Label>
                <div className="flex flex-wrap gap-2">
                  {GENRES.map(genre => (
                    <button
                      key={genre}
                      type="button"
                      onClick={() => toggleGenre(genre)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        (profileForm.genres || []).includes(genre)
                          ? 'bg-[#D4AF37] text-black'
                          : 'bg-[#27272A] text-zinc-400 hover:bg-[#3F3F46]'
                      }`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>
              )}
            </SectionCard>

            {/* Social Media */}
            <SectionCard title="Redes Sociales" icon={Instagram} description="Conectá con tu audiencia">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Instagram size={14} className="text-pink-500" /> Instagram</Label>
                  <Input
                    value={profileForm.instagram || ''}
                    onChange={(e) => setProfileForm({...profileForm, instagram: e.target.value})}
                    placeholder="@tu_usuario"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Facebook size={14} className="text-blue-500" /> Facebook</Label>
                  <Input
                    value={profileForm.facebook || ''}
                    onChange={(e) => setProfileForm({...profileForm, facebook: e.target.value})}
                    placeholder="URL de tu página"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Twitter size={14} className="text-sky-400" /> Twitter/X</Label>
                  <Input
                    value={profileForm.twitter || ''}
                    onChange={(e) => setProfileForm({...profileForm, twitter: e.target.value})}
                    placeholder="@tu_usuario"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" style={{color: '#ff0050'}}>
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                    TikTok
                  </Label>
                  <Input
                    value={profileForm.tiktok || ''}
                    onChange={(e) => setProfileForm({...profileForm, tiktok: e.target.value})}
                    placeholder="@tu_usuario"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Youtube size={14} className="text-red-500" /> YouTube</Label>
                  <Input
                    value={profileForm.youtube || ''}
                    onChange={(e) => setProfileForm({...profileForm, youtube: e.target.value})}
                    placeholder="URL de tu canal"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
              </div>
            </SectionCard>

            {/* Music Links */}
            <SectionCard title="Plataformas de Música" icon={Headphones} description="Donde escuchar tu música" hidden={isAgencyProfile}>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#1DB954"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                    Spotify
                  </Label>
                  <Input
                    value={profileForm.spotify_url || ''}
                    onChange={(e) => setProfileForm({...profileForm, spotify_url: e.target.value})}
                    placeholder="URL de tu perfil de Spotify"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#FF5500"><path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.102.007.201-.007c.063-.005.101-.03.126-.063.031-.048.054-.107.06-.199l.053-.501c.022-.245.063-.379.178-.379.14 0 .19.1.19.315v1.2c0 .245-.04.382-.17.382-.144 0-.19-.122-.19-.378v-.396c-.002-.044-.025-.078-.071-.078h-.285c-.047 0-.091.018-.091.064v.29c0 .599.15.92.586.92.47 0 .636-.361.636-.94v-1.16c0-.516-.133-.855-.557-.855-.365 0-.487.224-.531.424l-.018-.041-.008-.078c-.006-.048-.043-.08-.091-.08h-.286zm2.633-.007c-.057 0-.09.04-.09.091v2.82c0 .05.033.09.09.09h.286c.057 0 .091-.04.091-.09v-2.82c0-.051-.034-.091-.09-.091h-.287zm.803 0c-.058 0-.091.04-.091.091v2.82c0 .05.033.09.09.09h.87c.056 0 .09-.04.09-.09v-.286c0-.05-.034-.09-.09-.09h-.49v-2.444c0-.051-.035-.091-.091-.091h-.288zm1.424 0c-.057 0-.09.04-.09.091v2.82c0 .05.033.09.09.09h.883c.056 0 .09-.04.09-.09v-.286c0-.05-.034-.09-.09-.09h-.497v-.857h.393c.057 0 .09-.04.09-.09v-.287c0-.05-.033-.09-.09-.09h-.393v-.83h.497c.056 0 .09-.04.09-.09v-.2c0-.051-.034-.091-.09-.091h-.883zm1.632 0c-.057 0-.09.04-.09.091v2.82c0 .05.033.09.09.09h.287c.056 0 .09-.04.09-.09v-1.203l.606 1.234c.025.044.057.06.101.06h.287c.077 0 .112-.05.083-.118l-.605-1.163.57-1.099a.09.09 0 00-.08-.131h-.287a.11.11 0 00-.096.06l-.579 1.145v-1.605c0-.051-.034-.091-.09-.091h-.287zm1.87 0c-.057 0-.091.04-.091.091v2.82c0 .05.034.09.09.09h.287c.057 0 .09-.04.09-.09v-2.82c0-.051-.033-.091-.09-.091h-.286zm.886.918a.09.09 0 00-.09.09v.287a.09.09 0 00.09.09h.287a.09.09 0 00.09-.09v-.287a.09.09 0 00-.09-.09h-.287zm0 1.132a.09.09 0 00-.09.09v.287a.09.09 0 00.09.09h.287a.09.09 0 00.09-.09v-.287a.09.09 0 00-.09-.09h-.287z"/><path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm0 22.154c-5.602 0-10.154-4.552-10.154-10.154S6.398 1.846 12 1.846 22.154 6.398 22.154 12 17.602 22.154 12 22.154z"/></svg>
                    SoundCloud
                  </Label>
                  <Input
                    value={profileForm.soundcloud_url || ''}
                    onChange={(e) => setProfileForm({...profileForm, soundcloud_url: e.target.value})}
                    placeholder="URL de tu perfil de SoundCloud"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#52AAD3"><path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm-.042 6c3.331 0 6.042 2.691 6.042 6s-2.711 6-6.042 6S5.916 15.309 5.916 12 8.627 6 11.958 6zm0 1.5a4.5 4.5 0 100 9 4.5 4.5 0 000-9z"/></svg>
                    Mixcloud
                  </Label>
                  <Input
                    value={profileForm.mixcloud_url || ''}
                    onChange={(e) => setProfileForm({...profileForm, mixcloud_url: e.target.value})}
                    placeholder="URL de tu perfil de Mixcloud"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#00FF00"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm4.91 16.456c-.203.323-.638.42-.974.215-2.67-1.632-6.029-2.002-9.985-1.096-.382.088-.763-.152-.85-.534-.09-.38.151-.764.532-.851 4.33-.99 8.047-.564 11.062 1.266.336.204.416.652.215.974v.026zm1.31-3.102c-.257.408-.802.534-1.204.28-3.054-1.878-7.707-2.422-11.32-1.325-.462.14-.95-.122-1.09-.585-.14-.46.122-.95.584-1.089 4.13-1.257 9.265-.648 12.754 1.514.402.26.534.805.277 1.205zm.112-3.229C14.554 7.825 8.456 7.622 4.928 8.66c-.554.163-1.14-.154-1.303-.71-.163-.553.154-1.14.708-1.303 4.055-1.193 10.8-.963 15.06 1.53.497.297.663.946.368 1.443-.296.497-.946.662-1.443.366.014.008.014.008 0 0z"/></svg>
                    Beatport
                  </Label>
                  <Input
                    value={profileForm.beatport_url || ''}
                    onChange={(e) => setProfileForm({...profileForm, beatport_url: e.target.value})}
                    placeholder="URL de tu perfil de Beatport"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#1DA0C3"><path d="M0 4.8v14.4c0 2.65 2.15 4.8 4.8 4.8h14.4c2.65 0 4.8-2.15 4.8-4.8V4.8C24 2.15 21.85 0 19.2 0H4.8C2.15 0 0 2.15 0 4.8zm16.95 9.12l-1.59 1.35c-.51.43-1.2.67-1.92.67H5.93V8.06h7.51c.72 0 1.41.24 1.92.67l1.59 1.35c.51.43.81 1.05.81 1.71v.62c0 .66-.3 1.28-.81 1.71z"/></svg>
                    Bandcamp
                  </Label>
                  <Input
                    value={profileForm.bandcamp_url || ''}
                    onChange={(e) => setProfileForm({...profileForm, bandcamp_url: e.target.value})}
                    placeholder="URL de tu Bandcamp"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#fa243c"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09z"/></svg>
                    Apple Music
                  </Label>
                  <Input
                    value={profileForm.apple_music_url || ''}
                    onChange={(e) => setProfileForm({...profileForm, apple_music_url: e.target.value})}
                    placeholder="URL de tu Apple Music"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
              </div>
            </SectionCard>

            {/* Videos */}
            <SectionCard title="Videos" icon={Play} defaultOpen={false} description="Sets, live performances, promocionales">
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newVideoUrl}
                    onChange={(e) => setNewVideoUrl(e.target.value)}
                    placeholder="URL del video (YouTube, Vimeo, etc)"
                    className="bg-[#0A0A0A] border-[#27272A]"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addVideoUrl())}
                  />
                  <Button type="button" onClick={addVideoUrl} variant="outline" className="border-[#27272A]">
                    <Plus size={16} />
                  </Button>
                </div>
                {(profileForm.video_urls || []).map((url, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-[#1A1A1A] rounded-lg">
                    <Play size={16} className="text-[#D4AF37]" />
                    <a href={url} target="_blank" rel="noopener noreferrer" className="flex-1 text-sm truncate hover:text-[#D4AF37]">
                      {url}
                    </a>
                    <button onClick={() => removeVideoUrl(idx)} className="text-red-400 hover:text-red-300">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Photo Gallery */}
            <SectionCard title="Galería de Fotos" icon={Image} defaultOpen={false} description="Subí tus fotos promocionales (hasta 9)">
              <div className="space-y-3">
                <MultiImageUpload
                  value={profileForm.gallery_urls || []}
                  onChange={(items) => setProfileForm({ ...profileForm, gallery_urls: items })}
                  folder="dj_gallery"
                  max={9}
                  label="Subir fotos promocionales"
                  testIdPrefix="dj-gallery"
                />
                <p className="text-xs text-zinc-500">
                  Las fotos se hostean en nuestra CDN. No pegues links de Instagram, Drive o Dropbox: muchos servicios bloquean el hotlinking y la galería se rompe.
                </p>
              </div>
            </SectionCard>

            {/* Career Highlights */}
            <SectionCard title="Trayectoria" icon={Award} defaultOpen={false} description="Residencias, festivales, sellos" hidden={isAgencyProfile}>
              <div className="space-y-6">
                <ListManager 
                  items={profileForm.residencies || []}
                  setItems={(items) => setProfileForm({...profileForm, residencies: items})}
                  placeholder="Ej: Club Pachá, Crobar"
                  icon={Building2}
                  label="Residencias / Clubs"
                />
                
                <ListManager 
                  items={profileForm.festivals || []}
                  setItems={(items) => setProfileForm({...profileForm, festivals: items})}
                  placeholder="Ej: Lollapalooza Argentina"
                  icon={PartyPopper}
                  label="Festivales"
                />
                
                <ListManager 
                  items={profileForm.labels || []}
                  setItems={(items) => setProfileForm({...profileForm, labels: items})}
                  placeholder="Ej: Defected Records"
                  icon={Disc}
                  label="Sellos Discográficos"
                />
                
                <ListManager 
                  items={profileForm.awards || []}
                  setItems={(items) => setProfileForm({...profileForm, awards: items})}
                  placeholder="Ej: DJ Awards 2023 - Mejor DJ Emergente"
                  icon={Trophy}
                  label="Premios y Reconocimientos"
                />
              </div>
            </SectionCard>

            {/* Releases */}
            <SectionCard title="Releases / Tracks" icon={Disc3} defaultOpen={false} description="Tus producciones propias" hidden={isAgencyProfile}>
              <div className="space-y-4">
                <div className="grid md:grid-cols-4 gap-2">
                  <Input
                    value={newRelease.title}
                    onChange={(e) => setNewRelease({...newRelease, title: e.target.value})}
                    placeholder="Título del track"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                  <Input
                    value={newRelease.year}
                    onChange={(e) => setNewRelease({...newRelease, year: e.target.value})}
                    placeholder="Año"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                  <Input
                    value={newRelease.label}
                    onChange={(e) => setNewRelease({...newRelease, label: e.target.value})}
                    placeholder="Sello"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={newRelease.link}
                      onChange={(e) => setNewRelease({...newRelease, link: e.target.value})}
                      placeholder="Link"
                      className="bg-[#0A0A0A] border-[#27272A]"
                    />
                    <Button type="button" onClick={addRelease} variant="outline" className="border-[#27272A]">
                      <Plus size={16} />
                    </Button>
                  </div>
                </div>
                {(profileForm.releases || []).map((release, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-[#1A1A1A] rounded-lg">
                    <Music2 size={16} className="text-[#D4AF37]" />
                    <div className="flex-1">
                      <p className="font-medium">{release.title}</p>
                      <p className="text-xs text-zinc-500">{release.year} {release.label && `• ${release.label}`}</p>
                    </div>
                    {release.link && (
                      <a href={release.link} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37]">
                        <ExternalLink size={16} />
                      </a>
                    )}
                    <button onClick={() => removeRelease(idx)} className="text-red-400 hover:text-red-300">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Technical Rider */}
            <SectionCard title="Rider Técnico" icon={FileText} defaultOpen={false} description="Requerimientos para tus shows" hidden={isAgencyProfile}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Descripción del Rider</Label>
                  <Textarea
                    value={profileForm.technical_rider || ''}
                    onChange={(e) => setProfileForm({...profileForm, technical_rider: e.target.value})}
                    placeholder="Describí tus requerimientos técnicos: equipos, monitores, backline, etc..."
                    rows={6}
                    className="bg-[#0A0A0A] border-[#27272A] resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Link a Rider PDF (opcional)</Label>
                  <Input
                    value={profileForm.rider_pdf_url || ''}
                    onChange={(e) => setProfileForm({...profileForm, rider_pdf_url: e.target.value})}
                    placeholder="URL del PDF de tu rider técnico"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
              </div>
            </SectionCard>

            {/* Performance Style */}
            <SectionCard title="Estilo de Performance" icon={Mic2} defaultOpen={false} description="Cómo es tu show">
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Estilo de Performance</Label>
                    <Select 
                      value={profileForm.performance_style || ''} 
                      onValueChange={(v) => setProfileForm({...profileForm, performance_style: v})}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {PERFORMANCE_STYLES.map(style => (
                          <SelectItem key={style.value} value={style.value}>{style.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Duración típica del set</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={profileForm.set_duration_min || ''}
                        onChange={(e) => setProfileForm({...profileForm, set_duration_min: parseInt(e.target.value) || null})}
                        placeholder="Mín"
                        className="bg-[#0A0A0A] border-[#27272A]"
                      />
                      <span className="text-zinc-500">a</span>
                      <Input
                        type="number"
                        value={profileForm.set_duration_max || ''}
                        onChange={(e) => setProfileForm({...profileForm, set_duration_max: parseInt(e.target.value) || null})}
                        placeholder="Máx"
                        className="bg-[#0A0A0A] border-[#27272A]"
                      />
                      <span className="text-zinc-500">min</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Label>Habilidades Especiales</Label>
                  <div className="flex flex-wrap gap-2">
                    {SPECIAL_SKILLS.map(skill => (
                      <button
                        key={skill}
                        type="button"
                        onClick={() => toggleSkill(skill)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          (profileForm.special_skills || []).includes(skill)
                            ? 'bg-[#D4AF37] text-black'
                            : 'bg-[#27272A] text-zinc-400 hover:bg-[#3F3F46]'
                        }`}
                      >
                        {skill}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-[#1A1A1A] rounded-lg mt-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={profileForm.equipment_own || false}
                      onCheckedChange={(v) => setProfileForm({...profileForm, equipment_own: v})}
                    />
                    <Label>Tengo equipo propio</Label>
                  </div>
                </div>
                {profileForm.equipment_own && (
                  <div className="space-y-2">
                    <Label>Detalle del equipo</Label>
                    <Textarea
                      value={profileForm.equipment_details || ''}
                      onChange={(e) => setProfileForm({...profileForm, equipment_details: e.target.value})}
                      placeholder="CDJs 3000, DJM-900NXS2, controladora, etc..."
                      rows={3}
                      className="bg-[#0A0A0A] border-[#27272A] resize-none"
                    />
                  </div>
                )}
              </div>
            </SectionCard>

            {/* Rates & Availability */}
            <SectionCard title="Tarifas y Disponibilidad" icon={DollarSign} defaultOpen={false} description="Información comercial" hidden={isAgencyProfile}>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Caché Mínimo (ARS)</Label>
                  <Input
                    type="number"
                    value={profileForm.cache_min || ''}
                    onChange={(e) => setProfileForm({...profileForm, cache_min: parseFloat(e.target.value) || null})}
                    placeholder="Ej: 50000"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Caché Máximo (ARS)</Label>
                  <Input
                    type="number"
                    value={profileForm.cache_max || ''}
                    onChange={(e) => setProfileForm({...profileForm, cache_max: parseFloat(e.target.value) || null})}
                    placeholder="Ej: 150000"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Años de Experiencia</Label>
                  <Input
                    type="number"
                    value={profileForm.experience_years || ''}
                    onChange={(e) => setProfileForm({...profileForm, experience_years: parseInt(e.target.value) || null})}
                    placeholder="Ej: 5"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Año que comenzaste</Label>
                  <Input
                    type="number"
                    value={profileForm.started_year || ''}
                    onChange={(e) => setProfileForm({...profileForm, started_year: parseInt(e.target.value) || null})}
                    placeholder="Ej: 2015"
                    className="bg-[#0A0A0A] border-[#27272A]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Disponibilidad</Label>
                  <Select 
                    value={profileForm.availability || ''} 
                    onValueChange={(v) => setProfileForm({...profileForm, availability: v})}
                  >
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Fines de semana">Fines de semana</SelectItem>
                      <SelectItem value="Entre semana">Entre semana</SelectItem>
                      <SelectItem value="Cualquier día">Cualquier día</SelectItem>
                      <SelectItem value="A consultar">A consultar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-4 p-4 bg-[#1A1A1A] rounded-lg">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={profileForm.travel_available || false}
                    onCheckedChange={(v) => setProfileForm({...profileForm, travel_available: v})}
                  />
                  <Label className="flex items-center gap-2">
                    <Plane size={14} /> Disponible para viajar (nacional)
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={profileForm.international_available || false}
                    onCheckedChange={(v) => setProfileForm({...profileForm, international_available: v})}
                  />
                  <Label className="flex items-center gap-2">
                    <Globe size={14} /> Internacional
                  </Label>
                </div>
              </div>
            </SectionCard>

            {/* Stats */}
            <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-5">
              <div className="flex flex-wrap items-center gap-8">
                <div>
                  <p className="text-3xl font-black text-[#D4AF37]">{profile.profile_views || 0}</p>
                  <p className="text-sm text-zinc-500">Vistas del perfil</p>
                </div>
                {profile.featured && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37]">
                    <Sparkles size={14} />
                    <span className="text-sm font-medium">Destacado</span>
                  </div>
                )}
                <div className="ml-auto">
                  <p className="text-xs text-zinc-500">
                    Última actualización: {new Date(profile.updated_at || profile.created_at).toLocaleDateString('es-AR')}
                  </p>
                </div>
              </div>
            </div>

            {/* Save Button Fixed Bottom on Mobile */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-[#0A0A0A] border-t border-[#27272A]">
              <Button 
                onClick={handleUpdateProfile}
                disabled={submitting}
                className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold h-12"
              >
                {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                Guardar Cambios
              </Button>
            </div>
            <div className="h-20 md:hidden" /> {/* Spacer for fixed button */}
          </div>
        )}
      </div>
    </div>
  );
};

export default DJProfilePage;
